import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, MessageSquarePlus, MessagesSquare, TriangleAlert, ArrowLeft } from 'lucide-react';
import { Segmented, PageHeader, Button, Modal, Field, Input, Select, Textarea, EmptyState } from '../components/ui';
import { FormError } from '../components/forms/common';
import { ThreadRow } from '../components/messages/ThreadRow';
import { Thread } from '../components/messages/Thread';
import { messagesApi, type CommsTeam, type Viewer } from '../lib/comms';
import { TPageHead, TEmpty, TCard } from '../components/tenant/kit';
import { useResidentPortal } from '../lib/resident';

/**
 * Residents write to a team — the property manager, the building manager, lettings or
 * accounts — and each team answers its own queue (prototype §27a). Staff can add internal
 * notes the resident never sees, and close a conversation off when it is done.
 */
export default function MessagesPage() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const selected = params.get('c');
  const [team, setTeam] = useState<CommsTeam | ''>('');
  const [status, setStatus] = useState<'OPEN' | 'CLOSED'>('OPEN');
  const [awaiting, setAwaiting] = useState(false);
  const [composing, setComposing] = useState(false);

  const list = useQuery({
    queryKey: ['/messages', { team, status, awaiting }],
    queryFn: () => messagesApi.list({ team: team || undefined, status, awaiting: awaiting || undefined, perPage: 100 }),
    refetchInterval: 30_000,
  });
  const viewer = list.data?.viewer;
  const staff = viewer?.side === 'STAFF';
  const counts = list.data?.counts;

  const select = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set('c', id); else next.delete('c');
    setParams(next, { replace: false });
  };
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['/messages'] }); void qc.invalidateQueries({ queryKey: ['/my-day'] }); };
  // A resident reads Messages inside their portal: the same tab strip and width as Home, Rent, Repairs… (staff: unchanged).
  const portal = useResidentPortal();
  const canCompose = !!viewer && !staff && viewer.homes.length > 0;
  const compose = composing && viewer && <Compose viewer={viewer} onClose={() => setComposing(false)} onDone={(id) => { setComposing(false); refresh(); select(id); }} />;

  if (portal) {
    return (
      <div className="space-y-4 sm:space-y-5">
        <PageHeader title="Messages" />
        <TPageHead title="Messages" sub="Write to the team — the right person replies here."
          action={canCompose ? <Button icon="MessageSquarePlus" onClick={() => setComposing(true)}>New message</Button> : undefined} />
        {selected ? (<>
          <button type="button" onClick={() => select(null)} className="inline-flex min-h-[44px] items-center gap-2 rounded-full px-1 text-[14px] font-semibold text-bronze-700 hover:underline"><ArrowLeft className="h-4 w-4" />All messages</button>
          <Thread id={selected} onBack={() => select(null)} onChanged={refresh} />
        </>) : (<>
          <Segmented value={status} onChange={(v) => { setStatus(v); select(null); }} items={[{ key: 'OPEN' as typeof status, label: 'Open', count: counts ? counts.open : null }, { key: 'CLOSED' as typeof status, label: 'Closed', count: counts ? counts.closed : null }]} />
          {list.isLoading ? <TCard><div className="space-y-3">{[0, 1, 2].map((i) => <div key={i} className="h-14 animate-pulse rounded-[14px] bg-ink-100" />)}</div></TCard>
            : list.error ? <TEmpty icon={TriangleAlert} title="Could not load messages" body={(list.error as Error).message} />
            : !list.data?.items.length ? <TEmpty icon={MessagesSquare} title={status === 'CLOSED' ? 'Nothing closed yet' : 'No messages yet'} body={status === 'CLOSED' ? 'Finished conversations are kept here.' : 'Start a conversation and the right team will reply here.'} />
            : <div className="t-card t-rise divide-y divide-ink-100 overflow-hidden">{list.data.items.map((c) => <ThreadRow key={c.id} c={c} staff={false} active={false} onOpen={() => select(c.id)} />)}</div>}
        </>)}
        {compose}
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Messages"
        menu={[canCompose && { label: 'New message', icon: 'MessageSquarePlus', onClick: () => setComposing(true) }]}
      />

      <div className="surface mb-2.5 flex flex-wrap items-center gap-2 px-3 py-2">
        {staff && viewer && viewer.queues.length > 1 && (
          <div className="-mx-1 flex w-full overflow-x-auto px-1 sm:w-auto">
            <Segmented value={team} onChange={setTeam} items={[{ key: '' as typeof team, label: 'Everything' }, ...viewer.queues.map((q) => {
              const c = counts?.byTeam[q.team];
              return { key: q.team as typeof team, label: <>{q.label}{c && c.unread > 0 && <span className="inline-block h-1.5 w-1.5 rounded-full bg-bronze-500" />}</>, count: c && c.open > 0 ? c.open : null };
            })]} />
          </div>
        )}
        {staff && viewer && viewer.queues.length === 1 && <span className="text-[12.5px] text-ink-500">{viewer.queues[0]!.label} — what residents have sent your team.</span>}
        <div className="hidden flex-1 sm:block" />
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <Segmented value={status} onChange={setStatus} items={[{ key: 'OPEN' as typeof status, label: 'Open', count: counts ? counts.open : null }, { key: 'CLOSED' as typeof status, label: 'Closed', count: counts ? counts.closed : null }]} />
          {staff && (
            <label className="ml-auto inline-flex cursor-pointer items-center gap-1.5 text-[12px] text-ink-600 sm:ml-2">
              <input type="checkbox" checked={awaiting} onChange={(e) => setAwaiting(e.target.checked)} className="accent-bronze-500" />
              Waiting for us{counts?.awaiting ? ` (${counts.awaiting})` : ''}
            </label>
          )}
          {counts && counts.unread > 0 && <span className="whitespace-nowrap rounded-full bg-bronze-50 px-2 py-[3px] text-[11.5px] font-medium text-bronze-700">{counts.unread} unread</span>}
          {list.isFetching && <Loader2 className="h-4 w-4 animate-spin text-bronze-400" />}
        </div>
      </div>

      <div className="grid gap-2.5 lg:grid-cols-[minmax(300px,380px)_1fr]">
        <div className={`min-w-0 ${selected ? 'hidden lg:block' : ''}`}>
          {list.isLoading ? (
            <div className="surface space-y-3 p-4">{[0, 1, 2, 3].map((i) => <div key={i} className="h-14 animate-pulse rounded-xl bg-ink-100" />)}</div>
          ) : list.error ? (
            <EmptyState icon="TriangleAlert" title="Could not load messages" body={(list.error as Error).message} />
          ) : !list.data?.items.length ? (
            <EmptyState icon="MessagesSquare"
              title={status === 'CLOSED' ? 'Nothing closed yet' : staff ? 'No messages' : 'No messages yet'}
              body={status === 'CLOSED' ? 'Finish a conversation and it archives here, against the tenancy.'
                : staff ? (viewer?.queues.length ? 'Residents write to you from their portal. Nothing is waiting.' : 'Your account is not on a message queue.')
                : 'Start a conversation and the right team will reply here.'}
              action={!staff && viewer?.homes.length ? <Button icon="MessageSquarePlus" onClick={() => setComposing(true)}>New message</Button> : undefined} />
          ) : (
            <div className="surface divide-y divide-ink-100 overflow-hidden">
              {list.data.items.map((c) => <ThreadRow key={c.id} c={c} staff={!!staff} active={c.id === selected} onOpen={() => select(c.id)} />)}
            </div>
          )}
        </div>

        <div className={`min-w-0 ${selected ? '' : 'hidden lg:block'}`}>
          {selected ? (
            <Thread id={selected} onBack={() => select(null)} onChanged={refresh} />
          ) : (
            <div className="surface grid min-h-[420px] place-items-center p-8 text-center">
              <div>
                <MessageSquarePlus className="mx-auto h-6 w-6 text-bronze-400" />
                <p className="mt-3 text-[15px] font-semibold text-ink-900">Pick a conversation</p>
                <p className="mt-1 text-[12.5px] text-ink-500">{staff ? 'Unread threads are marked; the oldest unanswered also sit on My Day.' : 'Your conversations with the team appear on the left.'}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {compose}
    </div>
  );
}

function Compose({ viewer, onClose, onDone }: { viewer: Viewer; onClose: () => void; onDone: (id: string) => void }) {
  const [team, setTeam] = useState<CommsTeam>('PROPERTY');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [home, setHome] = useState(viewer.homes[0]?.tenancyId ?? '');
  const m = useMutation({ mutationFn: () => messagesApi.open({ team, subject: subject.trim(), body: body.trim(), tenancyId: home || null }), onSuccess: (d) => onDone(d.id) });
  const ready = useMemo(() => subject.trim().length > 0 && body.trim().length > 0, [subject, body]);
  return (
    <Modal open onClose={onClose} title="New message" size="lg"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button icon="Send" onClick={() => m.mutate()} disabled={!ready || m.isPending}>Send</Button></>}>
      <div className="space-y-2.5">
        <div>
          <span className="mb-1.5 block text-[12px] font-medium text-ink-600">Who should it go to?</span>
          <div className="grid gap-2 sm:grid-cols-2">
            {viewer.teams.map((t) => (
              <button key={t.team} type="button" onClick={() => setTeam(t.team)}
                className={`rounded-lg border px-3.5 py-2.5 text-left transition ${team === t.team ? 'border-bronze-500 bg-bronze-50 text-bronze-700' : 'border-ink-200 bg-white hover:border-ink-300'}`}>
                <div className="text-[13px] font-medium">{t.label}</div>
                <div className={`text-[11.5px] ${team === t.team ? 'text-bronze-700/80' : 'text-ink-500'}`}>{t.description}</div>
              </button>
            ))}
          </div>
        </div>
        {viewer.homes.length > 1 && (
          <Field label="About"><Select value={home} onChange={(e) => setHome(e.target.value)} className="w-full">{viewer.homes.map((h) => <option key={h.tenancyId} value={h.tenancyId}>{h.label}</option>)}</Select></Field>
        )}
        <Field label="Subject"><Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Kitchen tap dripping" maxLength={200} className="w-full" /></Field>
        <Field label="Message"><Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} placeholder="Tell us what is happening…" className="w-full" maxLength={5000} /></Field>
        {m.error && <FormError error={m.error} />}
      </div>
    </Modal>
  );
}
